import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  getRingRounds, getPartyRingStatuses, togglePremiumAccess, 
  updateRingRoundStatus, saveRingRound, updatePartyRingState, 
  RingRound, RoundStatus, PartyRingState, useRingStore,
  getFactChecks, updateFactCheckStatus, FactCheckRequest
} from "@/data/ringStore";
import { generateExport } from "@/utils/exportUtils";
import { 
  Download, PlusCircle, CheckCircle, XCircle, Search, 
  MessageSquare, History, Database, BarChart2, ShieldAlert, 
  AlertTriangle, Play, Pause, FileSpreadsheet, FileText, Calendar
} from "lucide-react";

export default function AdminRingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { isRingEnabled, toggleRingEnabled, isFactCheckEnabled, toggleFactCheckEnabled, rounds, factChecks, partyStatuses, arguments: storeArgs } = useRingStore();
  
  // Search states
  const [searchRound, setSearchRound] = useState("");
  const [searchParty, setSearchParty] = useState("");

  // Dialog states
  const [isRoundDialogOpen, setIsRoundDialogOpen] = useState(false);
  const [editingRound, setEditingRound] = useState<Partial<RingRound> | null>(null);
  
  // Moderation Dialog state
  const [modPartyDialog, setModPartyDialog] = useState<{acronym: string, action: 'warn' | 'block'} | null>(null);
  const [modMessage, setModMessage] = useState("");

  // Stats Selection
  const [selectedStatRoundId, setSelectedStatRoundId] = useState<string>(rounds[0]?.id || "");

  // Handlers for Rounds
  const handleSaveRound = () => {
    if (!editingRound?.category || !editingRound?.questionFr) {
      toast({ title: "Erreur", description: "Veuillez remplir au moins le thème et la question en français.", variant: "destructive" });
      return;
    }
    
    const newRound: RingRound = {
      id: editingRound.id || `r${Date.now()}`,
      roundNumber: editingRound.roundNumber || rounds.length + 1,
      category: editingRound.category,
      questionFr: editingRound.questionFr,
      questionAr: editingRound.questionAr || "",
      questionAmz: editingRound.questionAmz || "",
      status: editingRound.status || 'draft',
      startDate: editingRound.startDate,
      endDate: editingRound.endDate,
      daysLeft: editingRound.daysLeft || 0
    };
    
    saveRingRound(newRound);
    setIsRoundDialogOpen(false);
    toast({ title: "Round enregistré", description: `Le round a été enregistré en tant que ${newRound.status}.` });
  };

  const handleStatusChange = (id: string, currentStatus: RoundStatus) => {
    const nextStatus: Record<RoundStatus, RoundStatus> = {
      'draft': 'programmed',
      'programmed': 'open',
      'open': 'closed',
      'closed': 'open'
    };
    updateRingRoundStatus(id, nextStatus[currentStatus]);
    toast({ title: "Statut mis à jour", description: `Le round est maintenant ${nextStatus[currentStatus]}.` });
  };

  // Handlers for Party Moderation & Premium
  const handleTogglePremium = (acronym: string) => {
    togglePremiumAccess(acronym);
    toast({ title: "Accès Premium", description: "L'autorisation d'export a été modifiée." });
  };

  const executeModeration = () => {
    if (!modPartyDialog) return;
    const { acronym, action } = modPartyDialog;
    const newState: PartyRingState = action === 'warn' ? 'warned' : 'blocked';
    
    updatePartyRingState(acronym, newState, modMessage);
    setModPartyDialog(null);
    setModMessage("");
    toast({ 
      title: action === 'warn' ? "Avertissement envoyé" : "Parti bloqué", 
      description: `L'action de modération a été appliquée à ${acronym}.`,
      variant: action === 'block' ? 'destructive' : 'default'
    });
  };

  const handleUnblockParty = (acronym: string) => {
    updatePartyRingState(acronym, 'active', "");
    toast({ title: "Parti rétabli", description: `Le parti ${acronym} peut de nouveau participer au Ring.` });
  };

  // Export Logic
  const handleExportStats = (round: RingRound, format: 'excel' | 'pdf' | 'word') => {
    toast({ title: "Génération de l'export...", description: "Préparation des données des statistiques." });
    
    // Recalculate stats for export
    const roundArgs = storeArgs.filter(a => a.roundId === round.id && a.status === 'approved');
    const partiesData = [...new Set(roundArgs.map(a => a.partyAcronym))].map(acronym => {
      const arg = roundArgs.find(a => a.partyAcronym === acronym);
      const r = arg?.reactions || { convincing: 0, sourced: 0, demagogic: 0, offtopic: 0 };
      const score = (r.convincing * 2) + r.sourced - r.demagogic - (r.offtopic * 2);
      return {
        Parti: acronym,
        Convaincant: r.convincing,
        Source: r.sourced,
        Demagogique: r.demagogic,
        HorsSujet: r.offtopic,
        ScoreGlobal: score
      };
    }).sort((a, b) => b.ScoreGlobal - a.ScoreGlobal);

    generateExport(partiesData, `Stats_Ring_R${round.roundNumber}`, `Performances par Parti - Round ${round.roundNumber}`, format);
  };

  const handleExportHistory = (format: 'excel' | 'pdf' | 'word') => {
    toast({ title: "Export global...", description: "Génération de l'historique complet." });
    const data = rounds.map(r => ({
      Round: `R${r.roundNumber}`,
      Theme: r.category,
      Question_FR: r.questionFr,
      Statut: r.status.toUpperCase(),
      DateDebut: r.startDate || '-',
      DateFin: r.endDate || '-'
    }));
    generateExport(data, "Historique_Ring_Global", "Historique Global du Ring", format);
  };

  // Render Helpers
  const filteredRounds = rounds.filter(r => r.category.toLowerCase().includes(searchRound.toLowerCase()) || r.questionFr.toLowerCase().includes(searchRound.toLowerCase()));
  const filteredParties = partyStatuses.filter(p => p.partyAcronym.toLowerCase().includes(searchParty.toLowerCase()));
  const statRound = rounds.find(r => r.id === selectedStatRoundId) || rounds[0];
  const roundArgs = storeArgs.filter(a => a.roundId === statRound?.id && a.status === 'approved');
  const partiesStats = [...new Set(roundArgs.map(a => a.partyAcronym))].map(acronym => {
    const arg = roundArgs.find(a => a.partyAcronym === acronym);
    const r = arg?.reactions || { convincing: 0, sourced: 0, demagogic: 0, offtopic: 0 };
    const score = (r.convincing * 2) + r.sourced - r.demagogic - (r.offtopic * 2);
    return { acronym, r, score };
  }).sort((a, b) => b.score - a.score);

  const totalConvincing = roundArgs.reduce((acc, a) => acc + (a.reactions?.convincing || 0), 0);
  const totalSourced = roundArgs.reduce((acc, a) => acc + (a.reactions?.sourced || 0), 0);
  const totalDemagogic = roundArgs.reduce((acc, a) => acc + (a.reactions?.demagogic || 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-display font-bold text-foreground">Le Ring (Arène Politique)</h1>
          <p className="text-muted-foreground font-body text-sm mt-2">Gérez les sujets de débat, la modération des partis et les analyses statistiques.</p>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 bg-card px-4 py-2 rounded-lg border shadow-sm">
            <Label htmlFor="ring-toggle" className="font-medium cursor-pointer text-sm">
              {isRingEnabled ? "Visible sur l'accueil" : "Caché de l'accueil"}
            </Label>
            <Switch 
              id="ring-toggle" 
              checked={isRingEnabled} 
              onCheckedChange={(c) => {
                toggleRingEnabled(c);
                toast({ title: `Module Ring ${c ? 'activé' : 'désactivé'}`, description: "La modification est immédiatement en ligne." });
              }} 
            />
          </div>
          <div className="flex items-center gap-3 bg-card px-4 py-2 rounded-lg border shadow-sm border-blue-500/30">
            <Label htmlFor="fact-toggle" className="font-medium cursor-pointer text-sm text-blue-600">
              {isFactCheckEnabled ? "Fact-Checking Activé" : "Fact-Checking Désactivé"}
            </Label>
            <Switch 
              id="fact-toggle" 
              checked={isFactCheckEnabled} 
              onCheckedChange={(c) => {
                toggleFactCheckEnabled(c);
                toast({ title: `Fact-Checking ${c ? 'activé' : 'désactivé'}`, description: "Les citoyens peuvent/ne peuvent plus fact-checker." });
              }} 
            />
          </div>
        </div>
      </div>

      <Tabs value={searchParams.get("tab") || "rounds"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap gap-1 rounded-xl">
          <TabsTrigger value="rounds" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2">
            <MessageSquare className="w-4 h-4" /> Rounds & Sujets
          </TabsTrigger>
          <TabsTrigger value="stats" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2">
            <BarChart2 className="w-4 h-4" /> Stats & Exports
          </TabsTrigger>
          <TabsTrigger value="arguments" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2">
            <Database className="w-4 h-4" /> Argumentaires
          </TabsTrigger>
          <TabsTrigger value="parties" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2">
            <ShieldAlert className="w-4 h-4" /> Gestion des Partis
          </TabsTrigger>
          <TabsTrigger value="factchecks" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2 text-blue-600 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Search className="w-4 h-4" /> Fact-Checks
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg flex-1 font-semibold flex items-center gap-2 py-2">
            <History className="w-4 h-4" /> Historique
          </TabsTrigger>
        </TabsList>

        {/* 1. ONGLET ROUNDS */}
        <TabsContent value="rounds" className="mt-0">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Chercher un round..." className="pl-9" value={searchRound} onChange={(e) => setSearchRound(e.target.value)} />
            </div>
            
            <Dialog open={isRoundDialogOpen} onOpenChange={setIsRoundDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-white gap-2 w-full md:w-auto" onClick={() => setEditingRound({ status: 'draft', roundNumber: rounds.length + 1 })}>
                  <PlusCircle className="w-4 h-4" /> Nouveau Round
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRound?.id ? "Modifier le Round" : "Créer un Nouveau Round"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Thème / Catégorie</Label>
                      <Input value={editingRound?.category || ''} onChange={e => setEditingRound({...editingRound, category: e.target.value})} placeholder="Ex: Santé, Éducation..." />
                    </div>
                    <div className="space-y-2">
                      <Label>Statut Initial</Label>
                      <Select value={editingRound?.status} onValueChange={(v: RoundStatus) => setEditingRound({...editingRound, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Brouillon</SelectItem>
                          <SelectItem value="programmed">Programmé</SelectItem>
                          <SelectItem value="open">Ouvert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Question (Français)</Label>
                    <Textarea value={editingRound?.questionFr || ''} onChange={e => setEditingRound({...editingRound, questionFr: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Question (Arabe)</Label>
                    <Textarea dir="rtl" value={editingRound?.questionAr || ''} onChange={e => setEditingRound({...editingRound, questionAr: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Question (Tamazight)</Label>
                    <Textarea value={editingRound?.questionAmz || ''} onChange={e => setEditingRound({...editingRound, questionAmz: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de début (Optionnelle)</Label>
                      <Input type="date" value={editingRound?.startDate || ''} onChange={e => setEditingRound({...editingRound, startDate: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin (Optionnelle)</Label>
                      <Input type="date" value={editingRound?.endDate || ''} onChange={e => setEditingRound({...editingRound, endDate: e.target.value})} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsRoundDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleSaveRound}>Enregistrer le Round</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredRounds.map(round => (
              <Card key={round.id} className={`border-l-4 ${
                round.status === 'open' ? 'border-l-red-500' : 
                round.status === 'programmed' ? 'border-l-blue-500' : 
                round.status === 'draft' ? 'border-l-gray-400' : 'border-l-green-500'
              }`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">Round {round.roundNumber}</Badge>
                        <Badge className={`
                          ${round.status === 'open' ? 'bg-red-100 text-red-700' : ''}
                          ${round.status === 'programmed' ? 'bg-blue-100 text-blue-700' : ''}
                          ${round.status === 'closed' ? 'bg-green-100 text-green-700' : ''}
                          ${round.status === 'draft' ? 'bg-gray-100 text-gray-700' : ''}
                        `}>
                          {round.status.toUpperCase()}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{round.category}</CardTitle>
                    </div>
                    <Button variant="outline" size="icon" title="Avancer le statut" onClick={() => handleStatusChange(round.id, round.status)}>
                      {round.status === 'open' ? <Pause className="w-4 h-4 text-red-500" /> : <Play className="w-4 h-4 text-green-500" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm bg-muted/20 p-3 rounded-lg border border-border/50">
                    <p className="line-clamp-2"><strong>FR:</strong> {round.questionFr}</p>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground pt-2">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {round.startDate || 'Non défini'}</span>
                    <span>➜</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {round.endDate || 'Non défini'}</span>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="secondary" size="sm" className="w-full" onClick={() => { setEditingRound(round); setIsRoundDialogOpen(true); }}>Modifier</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* 1.5 ONGLET ARGUMENTAIRES */}
        <TabsContent value="arguments" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div>
                  <CardTitle>Suivi des Argumentaires</CardTitle>
                  <CardDescription>Consultez les contributions soumises par les partis pour chaque round.</CardDescription>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={selectedStatRoundId} onValueChange={setSelectedStatRoundId}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Sélectionner un round" />
                    </SelectTrigger>
                    <SelectContent>
                      {rounds.map(r => (
                        <SelectItem key={r.id} value={r.id}>Round {r.roundNumber}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative w-full sm:w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Filtrer par parti..." className="pl-9" value={searchParty} onChange={e => setSearchParty(e.target.value)} />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {statRound ? (
                <div className="space-y-6">
                  <div className="bg-muted/10 p-4 rounded-xl border border-border">
                    <h3 className="font-bold text-lg mb-1">Question: {statRound.questionFr}</h3>
                    <p className="text-sm text-muted-foreground">Thème: {statRound.category} | Statut: {statRound.status.toUpperCase()}</p>
                  </div>
                  
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    {roundArgs.length === 0 ? (
                      <div className="text-center p-8 text-muted-foreground">Aucun argument soumis pour ce round.</div>
                    ) : (
                      roundArgs
                        .filter(a => a.partyAcronym.toLowerCase().includes(searchParty.toLowerCase()))
                        .map((arg) => (
                        <div key={arg.id} className="p-4 rounded-xl border border-border/50 bg-card shadow-sm hover:border-primary/20 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">{arg.partyAcronym.substring(0, 3)}</div>
                              <h4 className="font-bold">{arg.partyAcronym}</h4>
                              <span className="text-xs text-muted-foreground ml-2">Soumis le: {new Date(arg.createdAt).toLocaleString('fr-FR')}</span>
                            </div>
                            <Badge variant="outline" className="bg-green-50 text-green-700">Publié</Badge>
                          </div>
                          <p className="text-sm mt-3 bg-muted/30 p-3 rounded-lg text-foreground/90 leading-relaxed whitespace-pre-wrap">
                            {arg.content}
                          </p>
                          <div className="mt-4 flex flex-wrap gap-3 pt-3 border-t border-border/50">
                            <div className="text-xs flex items-center gap-1 text-muted-foreground">
                              <span className="font-bold text-green-600">{arg.reactions?.convincing || 0}</span> Convaincants
                            </div>
                            <div className="text-xs flex items-center gap-1 text-muted-foreground">
                              <span className="font-bold text-blue-600">{arg.reactions?.sourced || 0}</span> Sourcés
                            </div>
                            <div className="text-xs flex items-center gap-1 text-muted-foreground">
                              <span className="font-bold text-orange-600">{arg.reactions?.demagogic || 0}</span> Démagos
                            </div>
                            <div className="text-xs flex items-center gap-1 text-muted-foreground">
                              <span className="font-bold text-gray-600">{arg.reactions?.offtopic || 0}</span> Hors-sujet
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 text-muted-foreground">Aucun round disponible.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. ONGLET STATS */}
        <TabsContent value="stats" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div>
                  <CardTitle>Statistiques du Débat</CardTitle>
                  <CardDescription>Visualisez les performances des partis pour un round spécifique.</CardDescription>
                </div>
                <Select value={selectedStatRoundId} onValueChange={setSelectedStatRoundId}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue placeholder="Sélectionner un round" />
                  </SelectTrigger>
                  <SelectContent>
                    {rounds.map(r => (
                      <SelectItem key={r.id} value={r.id}>Round {r.roundNumber} - {r.category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {statRound ? (
                <div className="space-y-8">
                  <div className="bg-muted/10 p-4 rounded-xl border border-border text-center">
                    <h3 className="font-bold text-lg mb-2">{statRound.questionFr}</h3>
                    <p className="text-sm text-muted-foreground">Statut: {statRound.status.toUpperCase()}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
                      <p className="text-sm font-bold text-primary">Partis Participants</p>
                      <p className="text-3xl font-black mt-2">{partiesStats.length}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-center">
                      <p className="text-sm font-bold text-green-700">Votes Convaincants</p>
                      <p className="text-3xl font-black mt-2 text-green-800">{totalConvincing}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-center">
                      <p className="text-sm font-bold text-blue-700">Arguments Sourcés</p>
                      <p className="text-3xl font-black mt-2 text-blue-800">{totalSourced}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-orange-50 border border-orange-200 text-center">
                      <p className="text-sm font-bold text-orange-700">Votes Démagos</p>
                      <p className="text-3xl font-black mt-2 text-orange-800">{totalDemagogic}</p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="font-bold text-lg mb-4">Performance par Parti</h4>
                    <div className="overflow-x-auto border rounded-xl shadow-sm">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-muted border-b text-xs uppercase font-bold text-muted-foreground">
                          <tr>
                            <th className="px-4 py-3">Parti</th>
                            <th className="px-4 py-3 text-center text-green-600">Convaincant (+2)</th>
                            <th className="px-4 py-3 text-center text-blue-600">Sourcé (+1)</th>
                            <th className="px-4 py-3 text-center text-orange-600">Démago (-1)</th>
                            <th className="px-4 py-3 text-center text-gray-600">Hors-sujet (-2)</th>
                            <th className="px-4 py-3 text-right">Score Global</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y bg-card">
                          {partiesStats.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                                Aucun parti n'a encore participé à ce round.
                              </td>
                            </tr>
                          ) : (
                            partiesStats.map((pStat, index) => (
                              <tr key={pStat.acronym} className={index === 0 ? "bg-amber-50/50 font-medium" : "hover:bg-muted/10"}>
                                <td className="px-4 py-3 font-black flex items-center gap-2">
                                  {index === 0 && <span className="text-amber-500">🏆</span>}
                                  {pStat.acronym}
                                </td>
                                <td className="px-4 py-3 text-center font-bold text-green-700">{pStat.r.convincing}</td>
                                <td className="px-4 py-3 text-center font-bold text-blue-700">{pStat.r.sourced}</td>
                                <td className="px-4 py-3 text-center font-bold text-orange-700">{pStat.r.demagogic}</td>
                                <td className="px-4 py-3 text-center font-bold text-gray-700">{pStat.r.offtopic}</td>
                                <td className="px-4 py-3 text-right font-black text-lg">{pStat.score > 0 ? `+${pStat.score}` : pStat.score}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-center gap-3 pt-6 border-t border-border/50">
                    <Button variant="outline" className="gap-2 border-green-200 text-green-700 hover:bg-green-50" onClick={() => handleExportStats(statRound, 'excel')}><FileSpreadsheet className="w-4 h-4"/> Excel</Button>
                    <Button variant="outline" className="gap-2 border-red-200 text-red-700 hover:bg-red-50" onClick={() => handleExportStats(statRound, 'pdf')}><FileText className="w-4 h-4"/> PDF</Button>
                    <Button variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => handleExportStats(statRound, 'word')}><FileText className="w-4 h-4"/> Word</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 text-muted-foreground">Aucun round disponible.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. ONGLET PARTIS (Modération) */}
        <TabsContent value="parties" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div>
                  <CardTitle>Modération & Droits Data Mining</CardTitle>
                  <CardDescription>Gérez l'accès des partis, envoyez des avertissements ou bloquez-les en cas de non respect de la charte.</CardDescription>
                </div>
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Rechercher un parti..." className="pl-9" value={searchParty} onChange={e => setSearchParty(e.target.value)} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredParties.map(p => (
                  <div key={p.partyAcronym} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border border-border/50 bg-card shadow-sm gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white
                        ${p.status === 'blocked' ? 'bg-red-500' : p.status === 'warned' ? 'bg-amber-500' : 'bg-emerald-500'}
                      `}>
                        {p.partyAcronym.substring(0, 3)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-lg">{p.partyAcronym}</h4>
                          {p.status === 'blocked' && <Badge variant="destructive">Bloqué</Badge>}
                          {p.status === 'warned' && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">Averti</Badge>}
                          {p.status === 'active' && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200">Actif</Badge>}
                        </div>
                        {p.status === 'warned' && <p className="text-xs text-amber-600 mt-1 italic">{p.warningMessage}</p>}
                        {p.charterSignedAt && <p className="text-xs text-emerald-600 mt-1 font-medium"><CheckCircle className="w-3 h-3 inline mr-1" />Charte signée le {new Date(p.charterSignedAt).toLocaleString('fr-FR')}</p>}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
                      <div className="flex items-center gap-2 mr-4 bg-muted/30 px-3 py-1.5 rounded-lg">
                        <Label className="text-xs font-semibold cursor-pointer">Export Data</Label>
                        <Switch checked={p.hasExportAccess} onCheckedChange={() => handleTogglePremium(p.partyAcronym)} />
                      </div>
                      
                      <div className="flex gap-2 w-full sm:w-auto">
                        {p.status === 'blocked' ? (
                          <Button variant="outline" className="w-full sm:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => handleUnblockParty(p.partyAcronym)}>
                            Débloquer
                          </Button>
                        ) : (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="w-full sm:w-auto border-red-200 text-red-700 hover:bg-red-50 gap-2">
                                <AlertTriangle className="w-4 h-4" /> Modérer
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Modération du parti : {p.partyAcronym}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label>Action à effectuer</Label>
                                  <Select onValueChange={(v: 'warn' | 'block') => setModPartyDialog({acronym: p.partyAcronym, action: v})}>
                                    <SelectTrigger><SelectValue placeholder="Choisir une action..." /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="warn">Envoyer un avertissement</SelectItem>
                                      <SelectItem value="block">Bloquer l'accès au Ring</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {modPartyDialog && (
                                  <div className="space-y-2">
                                    <Label>Motif (Sera affiché au parti)</Label>
                                    <Textarea value={modMessage} onChange={e => setModMessage(e.target.value)} placeholder="Veuillez respecter la charte..." />
                                  </div>
                                )}
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => {setModPartyDialog(null); setModMessage("");}}>Annuler</Button>
                                <Button variant={modPartyDialog?.action === 'block' ? 'destructive' : 'default'} onClick={executeModeration} disabled={!modPartyDialog || !modMessage}>
                                  Confirmer l'action
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. ONGLET HISTORIQUE */}
        <TabsContent value="history" className="mt-0">
          <Card>
            <CardHeader className="flex flex-row justify-between items-start">
              <div>
                <CardTitle>Historique Complet</CardTitle>
                <CardDescription>Consultez la liste de tous les rounds créés et exportez l'historique global.</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExportHistory('excel')}><FileSpreadsheet className="w-4 h-4 mr-2"/> CSV</Button>
                <Button variant="outline" size="sm" onClick={() => handleExportHistory('pdf')}><FileText className="w-4 h-4 mr-2"/> PDF</Button>
                <Button variant="outline" size="sm" onClick={() => handleExportHistory('word')}><FileText className="w-4 h-4 mr-2"/> DOC</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 border-b text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Round</th>
                      <th className="px-4 py-3">Thème</th>
                      <th className="px-4 py-3 w-1/3">Question (FR)</th>
                      <th className="px-4 py-3">Période</th>
                      <th className="px-4 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-card">
                    {rounds.map(r => (
                      <tr key={r.id} className="hover:bg-muted/20">
                        <td className="px-4 py-4 font-bold">R{r.roundNumber}</td>
                        <td className="px-4 py-4">{r.category}</td>
                        <td className="px-4 py-4 text-sm font-medium">{r.questionFr}</td>
                        <td className="px-4 py-4 text-xs text-muted-foreground">{r.startDate || '-'} / {r.endDate || '-'}</td>
                        <td className="px-4 py-4">
                          <Badge variant="outline" className={`
                            ${r.status === 'open' ? 'bg-red-50 text-red-700 border-red-200' : ''}
                            ${r.status === 'programmed' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                            ${r.status === 'closed' ? 'bg-green-50 text-green-700 border-green-200' : ''}
                            ${r.status === 'draft' ? 'bg-gray-50 text-gray-700 border-gray-200' : ''}
                          `}>
                            {r.status.toUpperCase()}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FACT CHECKS TAB */}
        <TabsContent value="factchecks" className="mt-0">
          <Card className="border-2 shadow-sm border-blue-500/20">
            <CardHeader className="bg-blue-50/50 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Search className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="text-blue-900">Modération des Fact-Checks communautaires</CardTitle>
                  <CardDescription>Validez ou rejetez les propositions de vérification soumises par les citoyens.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/30 border-b text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-6 py-4 font-bold">Date & Parti</th>
                      <th className="px-6 py-4 font-bold">Source fournie</th>
                      <th className="px-6 py-4 font-bold">Explication</th>
                      <th className="px-6 py-4 font-bold">Statut</th>
                      <th className="px-6 py-4 font-bold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {factChecks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Aucune demande de fact-check pour le moment.</td>
                      </tr>
                    ) : (
                      factChecks.map(fc => (
                        <tr key={fc.id} className="hover:bg-muted/10 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-bold">{fc.partyAcronym}</div>
                            <div className="text-xs text-muted-foreground">{new Date(fc.submittedAt).toLocaleDateString()}</div>
                          </td>
                          <td className="px-6 py-4">
                            <a href={fc.sourceUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline max-w-[200px] truncate block">
                              {fc.sourceUrl}
                            </a>
                          </td>
                          <td className="px-6 py-4 max-w-xs" title={fc.explanation}>
                            {fc.explanation ? (
                              fc.explanation.length > 60 ? fc.explanation.substring(0, 60) + "..." : fc.explanation
                            ) : <span className="italic text-muted-foreground">Aucune</span>}
                          </td>
                          <td className="px-6 py-4">
                            {fc.status === 'pending' ? <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">En attente</Badge> : 
                             fc.status === 'approved' ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Validé</Badge> : 
                             <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejeté</Badge>}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-green-200 text-green-700 hover:bg-green-50"
                                disabled={fc.status !== 'pending'}
                                onClick={() => {
                                  updateFactCheckStatus(fc.id, 'approved');
                                  toast({ title: "Fact-Check Validé", description: "L'affirmation sera marquée publiquement." });
                                }}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                disabled={fc.status !== 'pending'}
                                onClick={() => {
                                  updateFactCheckStatus(fc.id, 'rejected');
                                  toast({ title: "Fact-Check Rejeté", description: "Demande classée sans suite." });
                                }}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
